import { styled } from 'linaria/lib/react';
import { COLORS, FONTS } from '../../../../styles/constants';

// Animation timing
const AVATAR_ANIMATION_DURATION = '0.4s';
const AVATAR_ANIMATION_EASING = 'cubic-bezier(0.6, 0.94, 0.58, 1.16)';
const FADE_ANIMATION_DURATION = '0.4s';

// Main header container - always visible
export const StyledSparkCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0 12px 12px;
  min-height: 20px;
`;

// Left side container (avatar + text)
export const StyledSparkCardHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0; /* Allow text truncation */
`;

// Avatar container with grow-in transition
export const StyledSparkCardHeaderAvatar = styled.div<{ isVisible?: boolean }>`
  flex-shrink: 0;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transform: ${({ isVisible }) => (isVisible ? 'scale(1)' : 'scale(0)')};
  transition: opacity ${AVATAR_ANIMATION_DURATION} ${AVATAR_ANIMATION_EASING},
    transform ${AVATAR_ANIMATION_DURATION} ${AVATAR_ANIMATION_EASING};
`;

// Thought/fallback text
export const StyledSparkCardHeaderText = styled.div<{ fontColor?: string }>`
  color: ${({ fontColor }) => fontColor || COLORS.brown.dark[100]};
  cursor: pointer;
  font: 600 14px/140% ${FONTS.secondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
`;

// Timestamp fallback text - subdued style matching legacy cards
export const StyledSparkCardHeaderTimestamp = styled.div<{ fontColor?: string }>`
  color: ${({ fontColor }) => fontColor || COLORS.brown.dark[100]};
  cursor: pointer;
  font: 400 12px/140% ${FONTS.secondary};
  opacity: 0.6;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
`;

// Reaction button with fade-in transition
export const StyledSparkCardHeaderReactionButton = styled.button<{ isVisible?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  margin: 0;
  border: none;
  background: none;
  cursor: pointer;
  flex-shrink: 0;
  z-index: 1;

  svg {
    width: 24px;
    height: 24px;
    opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
    transition: opacity ${FADE_ANIMATION_DURATION} ease-out;
  }
`;

// ==========================================
// EXPANDED HEADER STYLES (Featured/Following)
// ==========================================

// Expanded header container - stacks vertically
export const StyledExpandedHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0 12px 20px;
`;

// Top row with avatar and user info
export const StyledExpandedHeaderTop = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

// Expanded avatar container with grow-in transition
export const StyledExpandedHeaderAvatar = styled.div<{ isVisible?: boolean }>`
  flex-shrink: 0;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transform: ${({ isVisible }) => (isVisible ? 'scale(1)' : 'scale(0)')};
  transition: opacity ${AVATAR_ANIMATION_DURATION} ${AVATAR_ANIMATION_EASING},
    transform ${AVATAR_ANIMATION_DURATION} ${AVATAR_ANIMATION_EASING};
`;

// User info container (name + bio)
export const StyledExpandedHeaderInfo = styled.div<{ isVisible?: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transition: opacity ${FADE_ANIMATION_DURATION} ease-out;
`;

// User name
export const StyledExpandedHeaderName = styled.span<{ fontColor?: string }>`
  color: ${({ fontColor }) => fontColor || COLORS.brown.dark[100]};
  font: 600 14px/1.4 ${FONTS.secondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// User bio
export const StyledExpandedHeaderBio = styled.span<{ fontColor?: string }>`
  color: ${({ fontColor }) => fontColor || COLORS.brown.dark[100]};
  font: 400 14px/1.4 ${FONTS.secondary};
  opacity: 0.6;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Bottom row with thought and reaction button
export const StyledExpandedHeaderBottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

// Thought text (below user info) with typewriter effect
export const StyledExpandedHeaderThought = styled.div<{ fontColor?: string }>`
  color: ${({ fontColor }) => fontColor || COLORS.brown.dark[100]};
  cursor: pointer;
  flex: 1;
  font: 600 14px/140% ${FONTS.secondary};
`;

// Timestamp fallback (below user info) - subdued style matching legacy cards
export const StyledExpandedHeaderTimestamp = styled.div<{ fontColor?: string }>`
  color: ${({ fontColor }) => fontColor || COLORS.brown.dark[100]};
  cursor: pointer;
  flex: 1;
  font: 400 12px/140% ${FONTS.secondary};
  opacity: 0.6;
`;

// Reaction button for expanded header
export const StyledExpandedHeaderReactionButton = styled.button<{ isVisible?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  margin: 0;
  border: none;
  background: none;
  cursor: pointer;
  flex-shrink: 0;
  z-index: 1;

  svg {
    width: 24px;
    height: 24px;
    opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
    transition: opacity ${FADE_ANIMATION_DURATION} ease-out;
  }
`;
